import { Fragment, useEffect, useState } from "react";
import { Helmet } from "react-helmet";

import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import {
  fetchUsers,
  selectSetups,
  createUser,
  removeUser,
  getUser,
} from "../setupSlices";
import Grid from "@mui/material/Grid";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import Paper from "@mui/material/Paper";

import {
  changePageTitle,
  selectPreference,
} from "../../preferences/preferencesSlice";
//import Avatar from "@mui/material/Avatar";
import {
  IconButton,
  Stack,
  Button,
  Typography,
  Tooltip,
  Box,
  Chip,
  Collapse,
  TableRow,
} from "@mui/material";
import {
  Add,
  Edit,
  Save,
  Delete,
  TrackChanges,
  KeyboardArrowDown,
  KeyboardArrowUp,
  Logout,
} from "@mui/icons-material";

import Accordion from "@mui/material/Accordion";
import { StyledAccordionSummary } from "../../../styles/componentStyled";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Form, Formik, FormikProps } from "formik";
import { FormikTextField } from "../../../components/Layout/FormikTextField";
import { StyledTableCell, StyledTableRow } from "../../../styles/tableStyles";
import TableSkeleton from "../../../components/Layout/TableSkeleton";
import { createUserSchema } from "../validation";
import { CreateUser, UserTypes } from "../../auth/types/authType";
import Paging from "../../../components/Layout/Paging";
import { selectAuth } from "../../auth/authSlice";
import { User } from "./User";
import { useTranslation } from "react-i18next";
import {
  fetchNotifications,
  selectProcesses,
} from "../../process/processSlices";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { NotificationStatus } from "../../process/types/processTypes";
import { CompanyFilter } from "../../../components/filter/CompanyFilter";
import { Company } from "../types/setupTypes";
import { isPrivilegedTransaction } from "../../../utils/authUtils";
import { Role } from "../../auth/types/authType";
import { RoleTypes } from "../../auth/types/roleTypes";
import { NotificationTable } from "../../notifications/NotificationTable";
import {
  ForceLogoutDialog,
  ForceLogoutTarget,
} from "./components/ForceLogoutDialog";

const defaultUser: CreateUser = {
  email: "",
  name: "",
};
interface CompanyUserProps {
  companyId?: string | undefined;
}
export const Users = ({ companyId }: CompanyUserProps) => {
  const [expanded, setExpanded] = useState(false);
  const [editExpanded, setEditExpanded] = useState(false);
  const [newUser, setNewUser] = useState(defaultUser);
  const [logoutDialog, setLogoutDialog] = useState<{
    targets?: ForceLogoutTarget[];
    allUsers?: boolean;
    allScope?: "global" | "company";
  } | null>(null);

  const { t } = useTranslation();
  const { t: ta } = useTranslation("auth");

  const dispatch = useAppDispatch();
  const {
    usersWithCount: { users, totalCount },
    selectedUser,
    loading,
  } = useAppSelector(selectSetups);
  const {
    notificationsWithCount: { notifications, totalCount: notifyTotalCount },
    loading: notifyLoading,
  } = useAppSelector(selectProcesses);
  const { user } = useAppSelector(selectAuth);
  const [company, setCompany] = useState<Company>();

  const { searchText } = useAppSelector(selectPreference);
  const [total, setTotal] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [currentPage, setCurrentPage] = useState(0);
  const [notifyRowsPerPage, setNotifyRowsPerPage] = useState(20);
  const [notifyCurrentPage, setNotifyCurrentPage] = useState(0);
  const [expandedRoleRows, setExpandedRoleRows] = useState<Set<string>>(
    new Set(),
  );

  const canViewNotifications = isPrivilegedTransaction(
    (user?.roles ?? []) as Role[],
    RoleTypes.ViewNotifications,
  );

  const toggleRoleRow = (id: string) =>
    setExpandedRoleRows((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  /** `Records.AddNewBasic` → `Records · Add New Basic`. */
  const prettyRole = (displayName?: string) =>
    (displayName ?? "")
      .split(".")
      .map((seg) => seg.replace(/([a-z\d])([A-Z])/g, "$1 $2"))
      .join(" · ");

  /** Chip color for a user status. */
  const statusColor = (
    status?: string,
  ): "success" | "warning" | "error" | "default" => {
    switch (status) {
      case "Active":
        return "success";
      case "Waiting":
        return "warning";
      case "Blocked":
      case "Disabled":
        return "error";
      default:
        return "default";
    }
  };

  useEffect(() => {
    const skipRows = currentPage * rowsPerPage;
    let compId = user && user.company ? user.company?._id : "";
    if (companyId) compId = companyId;
    if (company) compId = company._id;
    if (user)
      dispatch(
        fetchUsers({
          companyId: compId,
          skip: skipRows,
          take: rowsPerPage,
        }),
      );
  }, [
    dispatch,
    searchText,
    currentPage,
    rowsPerPage,
    user,
    companyId,
    company,
  ]);

  useEffect(() => {
    if (!canViewNotifications) return;
    const skipRows = notifyCurrentPage * notifyRowsPerPage;
    dispatch(
      fetchNotifications({
        skip: skipRows,
        take: notifyRowsPerPage,
        status: NotificationStatus.new,
      }),
    );
  }, [dispatch, notifyCurrentPage, notifyRowsPerPage, canViewNotifications]);

  // useEffect(() => {
  //   if (company) {
  //     dispatch(fetchUsers({ companyId: company._id }));
  //   } else if (companyId) {
  //     dispatch(fetchUsers({ companyId: companyId }));
  //   } else {
  //     dispatch(fetchUsers({ companyId: user?.company?._id }));
  //   }
  // }, [dispatch, company]);

  useEffect(() => {
    setTotal(totalCount as number);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalCount]);

  const notifyTotal = Math.max(
    notifyTotalCount ?? 0,
    notifyCurrentPage * notifyRowsPerPage + notifications.length,
  );

  const ToggleAccordion = () => {
    setExpanded(!expanded);
  };
  const ToggleEditAccordion = () => {
    setEditExpanded(!editExpanded);
  };
  const DeleteUser = (id: string) => {
    dispatch(removeUser(id));
  };
  const EditUser = (id: string) => {
    dispatch(getUser(id));
    setEditExpanded(true);
  };
  const ForceLogoutUser = (target: ForceLogoutTarget) => {
    setLogoutDialog({ targets: [target] });
  };
  const canForceLogoutAll =
    user?.userType === UserTypes.Super ||
    isPrivilegedTransaction(user?.roles as Role[], RoleTypes.ManageUsers);

  const ForceLogoutAllUsers = () => {
    setLogoutDialog({
      allUsers: true,
      allScope: user?.userType === UserTypes.Super ? "global" : "company",
    });
  };

  const ResetFields = () => {
    setNewUser(defaultUser);
    setExpanded(true);
  };

  // const RefreshList = () => {
  //   const skipRows = currentPage * rowsPerPage;
  //   let compId = user && user.company ? user.company?._id : "";
  //   if (companyId) compId = companyId;

  //   dispatch(
  //     fetchUsers({
  //       companyId: compId,
  //       refreshList: "refresh",
  //       searchText,
  //       skip: skipRows,
  //       take: rowsPerPage,
  //     })
  //   );
  // };
  return (
    <>
      <Helmet>
        <title> {`${ta("users")} | ${t("platform")}`}</title>
      </Helmet>
      <Box
        sx={{
          m: { xs: 0, md: 1 },
          px: { xs: 0, md: 2 },
        }}
      >
        {user && (user?.company || companyId) && (
          <>
            <Stack
              direction="row"
              justifyContent="space-between"
              justifyItems="center"
            >
              <Tooltip title="Add New User">
                <Button
                  color="secondary"
                  variant="contained"
                  onClick={ResetFields}
                >
                  <Add />
                </Button>
              </Tooltip>
              {canForceLogoutAll && (
                <Tooltip
                  title={
                    user?.userType === UserTypes.Super
                      ? "Sign out every user from all devices"
                      : "Sign out all users in your company from all devices"
                  }
                >
                  <Button
                    color="warning"
                    variant="outlined"
                    startIcon={<Logout />}
                    onClick={ForceLogoutAllUsers}
                  >
                    Force logout all
                  </Button>
                </Tooltip>
              )}
            </Stack>
            <CompanyFilter
              setCompany={setCompany}
              includeAll={false}
              includeModerator={true}
            />
            <Accordion sx={{ mt: 1 }} expanded={expanded}>
              <StyledAccordionSummary
                onClick={ToggleAccordion}
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel1a-content"
                id="panel1a-header"
              >
                <Typography>Add New User</Typography>
              </StyledAccordionSummary>
              <AccordionDetails>
                <Formik
                  enableReinitialize={true}
                  initialValues={newUser as CreateUser}
                  validationSchema={createUserSchema}
                  onSubmit={(values, actions) => {
                    actions.setSubmitting(false);
                    if (company && company._id) {
                      values = { ...values, companyId: company._id };
                    } else if (companyId) {
                      values = { ...values, companyId: companyId };
                    }
                    // if (user && user.company) {
                    //   values = { ...values, companyId: user.company._id };
                    // } else if (companyId) {
                    //   values = { ...values, companyId: companyId };
                    // }
                    dispatch(createUser(values));
                  }}
                >
                  {(props: FormikProps<CreateUser>) => (
                    <Form>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <FormikTextField formikKey="name" label="Name" />
                        </Grid>
                        <Grid item xs={6}>
                          <FormikTextField formikKey="email" label="Email" />
                        </Grid>
                      </Grid>

                      <br />
                      {/* {success && <Toast severity="success">{success.message}</Toast>}
              {error && <Toast severity="error">{error.message}</Toast>} */}
                      <Button
                        type="submit"
                        color="secondary"
                        variant="contained"
                        disabled={!props.isValid}
                      >
                        <Save />
                        Add
                      </Button>
                    </Form>
                  )}
                </Formik>
              </AccordionDetails>
            </Accordion>
          </>
        )}
        {selectedUser && selectedUser._id && selectedUser._id?.length > 0 && (
          <Accordion sx={{ mt: 1 }} expanded={editExpanded}>
            <StyledAccordionSummary
              onClick={ToggleEditAccordion}
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1a-content"
              id="panel1a-header"
            >
              <Typography>Edit User Profile</Typography>
            </StyledAccordionSummary>
            <AccordionDetails>
              <User userId={selectedUser._id as string} />
            </AccordionDetails>
          </Accordion>
        )}
        <Grid container justifyContent="flex-start" sx={{ mt: 1 }}>
          <TableContainer component={Paper}>
            <Table size="small" aria-label="simple table">
              <TableHead>
                <StyledTableRow>
                  <StyledTableCell>S.No</StyledTableCell>
                  <StyledTableCell>Name</StyledTableCell>
                  <StyledTableCell>Email</StyledTableCell>
                  <StyledTableCell>Status</StyledTableCell>
                  <StyledTableCell>Role</StyledTableCell>
                  <StyledTableCell>Company</StyledTableCell>
                  <StyledTableCell>Actions</StyledTableCell>
                </StyledTableRow>
              </TableHead>
              <TableBody>
                {loading === "pending" ? (
                  <TableSkeleton numRows={5} numColumns={7} />
                ) : (
                  users.map((row, index) => {
                    const roleCount = row.roles?.length ?? 0;
                    const open = expandedRoleRows.has(row._id as string);
                    return (
                      <Fragment key={row._id}>
                        <StyledTableRow>
                          <StyledTableCell component="th" scope="row">
                            {index + 1}
                          </StyledTableCell>
                          <StyledTableCell>{row.name}</StyledTableCell>
                          <StyledTableCell>{row.email}</StyledTableCell>
                          <StyledTableCell>
                            <Chip
                              size="small"
                              variant="outlined"
                              color={statusColor(row.status)}
                              label={row.status}
                              sx={{ fontWeight: 600 }}
                            />
                          </StyledTableCell>
                          <StyledTableCell>
                            <Chip
                              size="small"
                              clickable={roleCount > 0}
                              onClick={
                                roleCount > 0
                                  ? () => toggleRoleRow(row._id as string)
                                  : undefined
                              }
                              color={open ? "secondary" : "default"}
                              variant="outlined"
                              label={`${roleCount} ${
                                roleCount === 1 ? "role" : "roles"
                              }`}
                              icon={
                                roleCount > 0 ? (
                                  open ? (
                                    <KeyboardArrowUp fontSize="small" />
                                  ) : (
                                    <KeyboardArrowDown fontSize="small" />
                                  )
                                ) : undefined
                              }
                              sx={{ fontWeight: 600 }}
                            />
                          </StyledTableCell>
                          <StyledTableCell>
                            {row.company?.displayName}
                          </StyledTableCell>

                          <StyledTableCell>
                            <Stack
                              direction="row"
                              spacing={2}
                              alignItems="center"
                            >
                              <IconButton
                                color="primary"
                                onClick={() => EditUser(row._id as string)}
                                size="large"
                              >
                                <Edit />
                              </IconButton>
                              <Tooltip title="Force logout (sign out all sessions)">
                                <IconButton
                                  color="warning"
                                  onClick={() =>
                                    ForceLogoutUser({
                                      id: row._id as string,
                                      name: row.name,
                                      email: row.email,
                                    })
                                  }
                                  size="large"
                                >
                                  <Logout />
                                </IconButton>
                              </Tooltip>
                              <IconButton
                                color="secondary"
                                onClick={() => DeleteUser(row._id as string)}
                                size="large"
                              >
                                <Delete />
                              </IconButton>
                            </Stack>
                          </StyledTableCell>
                        </StyledTableRow>
                        <TableRow sx={{ display: open ? "table-row" : "none" }}>
                          <StyledTableCell
                            colSpan={7}
                            sx={{
                              py: 0,
                              border: open ? undefined : "none",
                            }}
                          >
                            <Collapse in={open} timeout="auto" unmountOnExit>
                              <Box
                                sx={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: 1,
                                  py: 1.5,
                                  px: 1,
                                }}
                              >
                                {row.roles?.map((role) => (
                                  <Chip
                                    key={role._id}
                                    size="small"
                                    variant="outlined"
                                    color="primary"
                                    title={role.displayName}
                                    label={prettyRole(role.displayName)}
                                    sx={{
                                      borderRadius: 1.5,
                                      bgcolor: "transparent",
                                    }}
                                  />
                                ))}
                              </Box>
                            </Collapse>
                          </StyledTableCell>
                        </TableRow>
                      </Fragment>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>

        <Stack spacing={1}>
          <Paging
            total={total}
            rowsPerPage={rowsPerPage}
            currentPage={currentPage}
            setRowsPerPage={setRowsPerPage}
            setCurrentPage={setCurrentPage}
          />
          <Typography variant="h6" color="textPrimary" component="div">
            Number of Users: {total}
          </Typography>
        </Stack>

        {canViewNotifications && (
        <Accordion>
          <StyledAccordionSummary
            expandIcon={<ExpandMore />}
            aria-controls="panel1a-content"
            id="panel1a-header"
          >
            <Typography variant="h6" component="div">
              <TrackChanges />
              User Actions
            </Typography>
          </StyledAccordionSummary>
          <AccordionDetails>
            <Stack spacing={1}>
              <NotificationTable
                notifications={notifications}
                loading={notifyLoading === "pending"}
                currentPage={notifyCurrentPage}
                rowsPerPage={notifyRowsPerPage}
                showActions={false}
              />
              <Paging
                total={notifyTotal}
                rowsPerPage={notifyRowsPerPage}
                currentPage={notifyCurrentPage}
                setRowsPerPage={setNotifyRowsPerPage}
                setCurrentPage={setNotifyCurrentPage}
              />
              <Typography variant="body2" color="textSecondary">
                {t("total")}: {notifyTotal}
              </Typography>
            </Stack>
          </AccordionDetails>
        </Accordion>
        )}
      </Box>
      {logoutDialog && (
        <ForceLogoutDialog
          open
          targets={logoutDialog.targets}
          allUsers={logoutDialog.allUsers}
          allScope={logoutDialog.allScope}
          onClose={() => setLogoutDialog(null)}
        />
      )}
    </>
  );
};
